import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import {
  AUDIO_TEXT_MAX_LENGTH,
  buildCorrectionRequest,
  type AudioTextCorrectionCopy,
  type AudioTextCorrectionRequest,
  type AudioTexts,
} from './audio-text-correction.contract';

type AudioTextCorrectionFormProps = {
  copy: AudioTextCorrectionCopy;
  current: AudioTexts;
  isInitialAnswer: boolean;
  isSaving: boolean;
  onSave: (request: AudioTextCorrectionRequest) => void;
  onCancel: () => void;
};

const fieldClass =
  'min-h-16 rounded-xl border-border/40 bg-background/50 px-3 py-2 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0';
const labelClass = 'text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70';

export function AudioTextCorrectionForm({ copy, current, isInitialAnswer, isSaving, onSave, onCancel }: AudioTextCorrectionFormProps) {
  const [draft, setDraft] = useState<AudioTexts>(current);
  const request = buildCorrectionRequest(current, draft);
  const isBlank = (value: string, original: string) => value.trim() === '' && original.trim() !== '';
  const hasBlank = isBlank(draft.phraseLocal, current.phraseLocal) || isBlank(draft.phraseFr, current.phraseFr);

  return (
    <div className="space-y-3 rounded-xl border border-primary/25 bg-primary/[0.03] p-4" data-testid="audio-text-correction-form">
      <div>
        <p className="text-sm font-semibold text-foreground">{copy.title}</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.hint}</p>
      </div>
      <label className="block space-y-1.5">
        <span className={labelClass}>{copy.localLabel}</span>
        <Textarea className={fieldClass} maxLength={AUDIO_TEXT_MAX_LENGTH} value={draft.phraseLocal} disabled={isSaving}
          onChange={(event) => setDraft((prev) => ({ ...prev, phraseLocal: event.target.value }))} />
      </label>
      <label className="block space-y-1.5">
        <span className={labelClass}>{isInitialAnswer ? copy.questionLabel : copy.frenchLabel}</span>
        <Textarea className={fieldClass} maxLength={AUDIO_TEXT_MAX_LENGTH} value={draft.phraseFr} disabled={isSaving}
          onChange={(event) => setDraft((prev) => ({ ...prev, phraseFr: event.target.value }))} />
      </label>
      {hasBlank && <p className="text-xs text-danger">{copy.invalid}</p>}
      {request === null && !hasBlank && <p className="text-xs text-muted-foreground">{copy.nothingChanged}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={isSaving}
          className="rounded-full border border-border/40 px-3.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40">
          {copy.cancel}
        </button>
        <button type="button" disabled={isSaving || hasBlank || request === null} onClick={() => request && onSave(request)}
          className="rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40">
          {isSaving ? copy.saving : copy.save}
        </button>
      </div>
    </div>
  );
}
